// Cycle-16 recording-playback panel (`Recording Playback` Figma frame
// `2304:42939`). Rendered after the user finishes a voice recording but
// before sending — the host owns the audio buffer; this primitive shows
// the waveform, play/pause, cancel, and send affordances.

import React, { useMemo } from 'react';
import { Pressable, StyleSheet, Text, type TextStyle, View } from 'react-native';
import { Pause, Play } from 'lucide-react-native';
import { useColorScheme } from '../../foundations/colorScheme';
import { tokens } from '../../design-system/tokens';
import { formatDuration, snapProgressToFrame } from '../messageFormatters';

export interface RecordingPlaybackProps {
  durationMs: number;
  waveform: ReadonlyArray<number>;
  progress: number;
  isPlaying: boolean;
  onTogglePlay: () => void;
  onCancel: () => void;
  onSend: () => void;
  testID?: string | undefined;
}

export const RecordingPlayback = React.memo<RecordingPlaybackProps>(
  ({ durationMs, waveform, progress, isPlaying, onTogglePlay, onCancel, onSend, testID }) => {
    const scheme = useColorScheme();
    const colorScheme: 'light' | 'dark' = scheme === 'dark' ? 'dark' : 'light';
    const colors = tokens.color.semantic[colorScheme];

    const snappedProgress = useMemo(
      () => snapProgressToFrame(progress, durationMs),
      [progress, durationMs],
    );

    const playedBars = Math.max(1, Math.round(waveform.length * snappedProgress));

    const cancelTextStyle: TextStyle = { color: colors.text.secondary, fontWeight: '500' };
    const sendTextStyle: TextStyle = { color: colors.text.onAccent, fontWeight: '600' };
    const durationTextStyle: TextStyle = { color: colors.text.secondary, fontWeight: '500' };

    return (
      <View
        style={[
          styles.container,
          { backgroundColor: colors.surface.secondary, borderColor: colors.border.secondary },
        ]}
        testID={testID}
      >
        <Pressable
          onPress={onTogglePlay}
          accessibilityRole="button"
          accessibilityLabel={isPlaying ? 'Pause playback' : 'Play recording'}
          style={[styles.playButton, { backgroundColor: colors.surface.tertiary }]}
        >
          {isPlaying ? (
            <Pause size={16} strokeWidth={2} color={colors.text.primary} fill={colors.text.primary} />
          ) : (
            <Play size={16} strokeWidth={2} color={colors.text.primary} fill={colors.text.primary} />
          )}
        </Pressable>
        <View style={styles.waveform}>
          {waveform.map((value, index) => {
            const heightFraction = Math.min(1, Math.max(0.08, value));
            const isPlayed = index < playedBars;
            return (
              <View
                key={index}
                style={[
                  styles.bar,
                  {
                    height: 24 * heightFraction,
                    backgroundColor: isPlayed ? colors.accent.indigo : colors.border.primary,
                  },
                ]}
              />
            );
          })}
        </View>
        <Text style={[styles.duration, durationTextStyle]}>{formatDuration(durationMs)}</Text>
        <Pressable
          onPress={onCancel}
          accessibilityRole="button"
          accessibilityLabel="Cancel recording"
          style={styles.cancelButton}
        >
          <Text style={[styles.cancelText, cancelTextStyle]}>Cancel</Text>
        </Pressable>
        <Pressable
          onPress={onSend}
          accessibilityRole="button"
          accessibilityLabel="Send recording"
          style={[styles.sendButton, { backgroundColor: colors.accent.indigo }]}
        >
          <Text style={[styles.sendText, sendTextStyle]}>Send</Text>
        </Pressable>
      </View>
    );
  },
);

RecordingPlayback.displayName = 'RecordingPlayback';

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.sm,
    paddingHorizontal: tokens.spacing.md,
    paddingVertical: tokens.spacing.sm,
    borderRadius: tokens.radius.lg,
    borderWidth: StyleSheet.hairlineWidth,
  },
  playButton: {
    width: 32,
    height: 32,
    borderRadius: tokens.radius.pill,
    alignItems: 'center',
    justifyContent: 'center',
  },
  playGlyph: {
    fontSize: tokens.typography.fontSize.footnote,
  },
  waveform: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
    height: 24,
  },
  bar: {
    width: 2,
    borderRadius: 1,
  },
  duration: {
    fontFamily: tokens.typography.fontFamily.base,
    fontSize: tokens.typography.fontSize.caption,
    minWidth: 36,
    textAlign: 'right',
  },
  cancelButton: {
    paddingHorizontal: tokens.spacing.sm,
  },
  cancelText: {
    fontFamily: tokens.typography.fontFamily.base,
    fontSize: tokens.typography.fontSize.footnote,
  },
  sendButton: {
    paddingHorizontal: tokens.spacing.md,
    paddingVertical: tokens.spacing.xs,
    borderRadius: tokens.radius.pill,
  },
  sendText: {
    fontFamily: tokens.typography.fontFamily.base,
    fontSize: tokens.typography.fontSize.footnote,
  },
});
